import * as Hooks from 'preact/hooks';

export type UndoState<S> = {
    undo?: () => void;
    redo?: () => void;
    current: S;
    pushState: Hooks.StateUpdater<S>;
    clear: Hooks.StateUpdater<S>;
};

export function useUndoState<S>(initialState: S): UndoState<S> {
    const [current, setCurrent] = Hooks.useState(initialState);

    const [undoStack, setUndoStack] = Hooks.useState<S[]>([]);
    const [redoStack, setRedoStack] = Hooks.useState<S[]>([]);

    const undo = Hooks.useMemo(
        () =>
            undoStack.length > 0
                ? () => {
                      const newUndoStack = undoStack.slice();
                      const newRedoStack = redoStack.slice();
                      const undo = newUndoStack.pop() as S;
                      newRedoStack.push(current);
                      setCurrent(undo);
                      setUndoStack(newUndoStack);
                      setRedoStack(newRedoStack);
                  }
                : undefined,
        [undoStack, redoStack, current],
    );

    const redo = Hooks.useMemo(
        () =>
            redoStack.length > 0
                ? () => {
                      const newUndoStack = undoStack.slice();
                      const newRedoStack = redoStack.slice();
                      const redo = newRedoStack.pop() as S;
                      newUndoStack.push(current);
                      setCurrent(redo);
                      setUndoStack(newUndoStack);
                      setRedoStack(newRedoStack);
                  }
                : undefined,
        [undoStack, redoStack, current],
    );

    const pushState = Hooks.useCallback(
        (value: S | ((prevState: S) => S)) => {
            const newUndoStack = undoStack.slice();
            newUndoStack.push(current);
            setCurrent(value);
            setUndoStack(newUndoStack);
            setRedoStack([]);
        },
        [undoStack, current],
    );

    const clear = Hooks.useCallback((value: S | ((prevState: S) => S)) => {
        setCurrent(value);
        setUndoStack([]);
        setRedoStack([]);
    }, []);

    return Hooks.useMemo(() => {
        return { undo, redo, current, pushState, clear };
    }, [undo, redo, current, pushState, clear]);
}
